import { useState, useRef, useEffect, useContext, ChangeEvent } from 'react'
import { MdSend, MdAttachFile, MdMic, MdClose } from 'react-icons/md'
import { SocketContext } from '@/lib/providers/socket-provider'
import {
    InternalMessage,
    uploadFiles,
} from '@/lib/api/internal-chat/internal-chat-api'
import ReplyPreview from './ReplyPreview'
import { AudioRecorder } from '@/components/audio-recorder/audio-recorder'
import { useAutoResizeTextarea } from '@/lib/hooks/use-auto-resize-textarea'

interface MessageInputProps {
    conversationId: number
    replyingTo: InternalMessage | null
    onCancelReply: () => void
    onSend: (
        content: string,
        replyToId?: number,
        attachments?: string[]
    ) => Promise<void>
    disabled?: boolean
    hideMediaControls?: boolean
}

interface FilePreview {
    file: File
    preview: string | null
    type: 'image' | 'audio' | 'video' | 'file'
}

const getFileType = (file: File): 'image' | 'audio' | 'video' | 'file' => {
    if (file.type.startsWith('image/')) return 'image'
    if (file.type.startsWith('audio/')) return 'audio'
    if (file.type.startsWith('video/')) return 'video'
    return 'file'
}

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function MessageInput({
    conversationId,
    replyingTo,
    onCancelReply,
    onSend,
    disabled,
    hideMediaControls,
}: MessageInputProps) {
    const [content, setContent] = useState('')
    const [sending, setSending] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<FilePreview[]>([])
    const [uploading, setUploading] = useState(false)
    const [recording, setRecording] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Campo cresce com o conteúdo; começa com 3 linhas para caber uma
    // mensagem inteira sem rolagem.
    const { ref: textareaRef } = useAutoResizeTextarea(content, {
        minHeight: 88,
        maxHeight: 200,
    })
    const fileInputRef = useRef<HTMLInputElement>(null)
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const { socket, isConnected } = useContext(SocketContext)

    // Focus textarea when replying
    useEffect(() => {
        if (replyingTo && textareaRef.current) {
            textareaRef.current.focus()
        }
    }, [replyingTo])

    // Cleanup file previews on unmount
    useEffect(() => {
        return () => {
            selectedFiles.forEach(f => {
                if (f.preview) URL.revokeObjectURL(f.preview)
            })
        }
    }, [])

    const handleTyping = () => {
        if (!socket || !isConnected) return

        socket.emit('internal-chat:typing', {
            conversationId,
            isTyping: true,
        })

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current)
        }

        typingTimeoutRef.current = setTimeout(() => {
            socket.emit('internal-chat:typing', {
                conversationId,
                isTyping: false,
            })
        }, 2000)
    }

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return

        const remainingSlots = 5 - selectedFiles.length
        const filesToAdd = files.slice(0, remainingSlots)

        const newPreviews: FilePreview[] = filesToAdd.map(file => {
            const type = getFileType(file)
            let preview: string | null = null

            if (type === 'image') {
                preview = URL.createObjectURL(file)
            }

            return { file, preview, type }
        })

        setSelectedFiles(prev => [...prev, ...newPreviews])
        setError(null)

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const removeFile = (index: number) => {
        setSelectedFiles(prev => {
            const newFiles = [...prev]
            const removed = newFiles.splice(index, 1)[0]
            if (removed.preview) {
                URL.revokeObjectURL(removed.preview)
            }
            return newFiles
        })
    }

    const handleRecordingComplete = (blob: Blob, duration: number) => {
        const file = new File([blob], `audio_${Date.now()}.webm`, {
            type: blob.type || 'audio/webm',
        })
        const preview: FilePreview = { file, preview: null, type: 'audio' }
        setSelectedFiles(prev => [...prev, preview])
        setRecording(false)
    }

    const handleRecordingError = (errorMsg: string) => {
        setError(errorMsg)
        setRecording(false)
    }

    const handleSend = async () => {
        const trimmedContent = content.trim()
        const hasContent = trimmedContent || selectedFiles.length > 0

        if (!hasContent || sending || disabled || uploading) return

        setSending(true)
        setError(null)

        try {
            let attachments: string[] = []

            // Upload files first
            if (selectedFiles.length > 0) {
                setUploading(true)
                const response = await uploadFiles(
                    selectedFiles.map(f => f.file)
                )
                setUploading(false)

                if (response.success && response.result?.files) {
                    attachments = response.result.files.map(f => f.url)
                } else {
                    setError(response.error || 'Erro ao enviar arquivos')
                    setSending(false)
                    return
                }
            }

            await onSend(trimmedContent, replyingTo?.id, attachments)

            // Clear state
            setContent('')
            selectedFiles.forEach(f => {
                if (f.preview) URL.revokeObjectURL(f.preview)
            })
            setSelectedFiles([])

            // Stop typing indicator
            if (socket && isConnected) {
                socket.emit('internal-chat:typing', {
                    conversationId,
                    isTyping: false,
                })
            }

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current)
            }
        } catch (err) {
            setError('Erro ao enviar mensagem')
        } finally {
            setSending(false)
            setUploading(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const canSend =
        (content.trim() || selectedFiles.length > 0) &&
        !disabled &&
        !sending &&
        !uploading &&
        !recording

    return (
        <div className="border-t border-base-300 bg-base-100">
            {/* Reply preview */}
            {replyingTo && (
                <ReplyPreview message={replyingTo} onCancel={onCancelReply} />
            )}

            {/* Error message */}
            {error && (
                <div className="px-3 pt-2">
                    <div className="alert alert-error text-sm py-2">
                        <span>{error}</span>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            onClick={() => setError(null)}
                        >
                            <MdClose />
                        </button>
                    </div>
                </div>
            )}

            {/* File previews */}
            {selectedFiles.length > 0 && !recording && (
                <div className="px-3 pt-3 flex flex-wrap gap-2">
                    {selectedFiles.map((item, index) => (
                        <div
                            key={index}
                            className="relative bg-base-200 rounded-lg p-2 flex items-center gap-2 max-w-[200px]"
                        >
                            {item.type === 'image' && item.preview ? (
                                <img
                                    src={item.preview}
                                    alt="Preview"
                                    className="w-12 h-12 object-cover rounded"
                                />
                            ) : (
                                <div className="w-12 h-12 bg-base-300 rounded flex items-center justify-center">
                                    <MdAttachFile className="text-xl text-base-content/50" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs truncate">
                                    {item.file.name}
                                </p>
                                <p className="text-xs text-base-content/50">
                                    {formatFileSize(item.file.size)}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs btn-circle absolute -top-1 -right-1 bg-base-300"
                                onClick={() => removeFile(index)}
                            >
                                <MdClose className="text-sm" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Audio recorder */}
            {recording && (
                <div className="p-3">
                    <AudioRecorder
                        onRecordingComplete={handleRecordingComplete}
                        onError={handleRecordingError}
                        maxDuration={300}
                        className="max-w-md mx-auto"
                    />
                    <div className="flex justify-center mt-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-error"
                            onClick={() => setRecording(false)}
                        >
                            <MdClose className="text-lg" />
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* Input area */}
            {!recording && (
                <div className="p-3 flex items-end gap-2">
                    {!hideMediaControls && (
                        <>
                            {/* File input (hidden) */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                multiple
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                onChange={handleFileSelect}
                            />

                            {/* Attachment button */}
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={
                                    disabled ||
                                    sending ||
                                    uploading ||
                                    selectedFiles.length >= 5
                                }
                                title="Anexar arquivo"
                            >
                                <MdAttachFile className="text-lg" />
                            </button>

                            {/* Audio button */}
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setRecording(true)}
                                disabled={disabled || sending || uploading}
                                title="Gravar audio"
                            >
                                <MdMic className="text-lg" />
                            </button>
                        </>
                    )}

                    <textarea
                        ref={textareaRef}
                        className="textarea textarea-bordered flex-1 resize-none py-2 leading-relaxed"
                        placeholder="Digite uma mensagem... (Shift+Enter para quebrar linha)"
                        value={content}
                        onChange={e => {
                            setContent(e.target.value)
                            handleTyping()
                        }}
                        onKeyDown={handleKeyDown}
                        disabled={disabled || sending || uploading}
                    />

                    <button
                        type="button"
                        className="btn btn-secondary btn-square"
                        onClick={handleSend}
                        disabled={!canSend}
                    >
                        {sending || uploading ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSend className="text-lg" />
                        )}
                    </button>
                </div>
            )}

            {/* File count indicator */}
            {selectedFiles.length > 0 && !recording && (
                <div className="px-3 pb-2 text-xs text-base-content/50">
                    {selectedFiles.length}/5 arquivos selecionados
                </div>
            )}
        </div>
    )
}
